import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { BlockRenderer } from '@/blocks/BlockRenderer';
import { Breadcrumbs } from '@/components/common/Layout';
import { QueryState } from '@/components/common/Query';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useSeo } from '@/lib/seo';
import type { PageData } from '@/lib/types';

export default function Home() {
  const q = useQuery({ queryKey: ['page', 'home'], queryFn: () => api.get<PageData>('/api/public/home').then((r) => r.data) });
  const { L } = useI18n();
  useSeo({ title: L(q.data?.seo?.title) || undefined, description: L(q.data?.seo?.description) || undefined, image: q.data?.seo?.ogImage });
  return <QueryState query={q} loading={<BlockRenderer loading />}>{(page) => <BlockRenderer blocks={page.blocks} />}</QueryState>;
}

/** Admin-created pages rendered from published blocks, e.g. /about-us or /privacy-policy. */
export function CustomPage() {
  const { slug = '' } = useParams();
  const q = useQuery({ queryKey: ['page', slug], queryFn: () => api.get<PageData>(`/api/public/pages/${encodeURIComponent(slug)}`).then((r) => r.data) });
  const { L } = useI18n();
  useSeo({ title: L(q.data?.seo?.title) || L(q.data?.title), description: L(q.data?.seo?.description) || undefined, noindex: q.data?.seo?.noindex });
  return (
    <QueryState query={q} loading={<div className="container py-16"><div className="skeleton h-10 w-1/2 rounded-xl" /><div className="skeleton mt-6 h-64 rounded-xl" /></div>}>
      {(page) => (
        <>
          {page.blocks[0]?.type !== 'hero' && (
            <header className="border-b border-line bg-white">
              <div className={cn('container py-8', page.pageType === 'legal' && 'max-w-3xl')}>
                <Breadcrumbs items={[{ label: L(page.title) }]} />
                <h1 className="font-display text-3xl font-bold text-ink md:text-4xl">{L(page.title)}</h1>
                {L(page.seo?.description) && page.pageType !== 'legal' && <p className="mt-1.5 max-w-2xl text-ink-muted">{L(page.seo?.description)}</p>}
              </div>
            </header>
          )}
          <BlockRenderer blocks={page.blocks} />
        </>
      )}
    </QueryState>
  );
}
